import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import { color, tooltipStyle, legendStyle } from "./palette";
import { GlassDefs, glassFill, GLASS_STROKE } from "./GlassDefs";

function Panel({ title, subtitle, children, testid, actions }) {
  return (
    <section className="glass-panel p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * Bar + Line combo chart on a single shared axis.
 * Props:
 *   data: [{ name, <barKey>, <lineKey> }]
 *   barKey: dataKey for bars (default "units")
 *   lineKey: dataKey for the line (default "surveys")
 *   barLabel / lineLabel: legend labels
 *   xAxisAngle (default -20)
 */
export default function ComposedChartCard({
  title,
  subtitle,
  testid,
  actions,
  data,
  barKey = "units",
  lineKey = "surveys",
  barLabel = "Units sold",
  lineLabel = "Surveys",
  height = "h-64 sm:h-80",
  xAxisAngle = -20,
  emptyText = "No data yet",
}) {
  const hasData = Array.isArray(data) && data.length > 0;
  return (
    <Panel title={title} subtitle={subtitle} testid={testid} actions={actions}>
      <div className={height}>
        {!hasData ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500 font-mono uppercase tracking-wider">
            {emptyText}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 16, right: 24, left: 0, bottom: xAxisAngle ? 60 : 5 }}
            >
              <GlassDefs />
              <CartesianGrid stroke="rgba(100,116,139,0.18)" strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={xAxisAngle}
                textAnchor={xAxisAngle ? "end" : "middle"}
                height={xAxisAngle ? 70 : 30}
                stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fontFamily: "Inter" }}
                stroke="rgba(100,116,139,0.35)" axisLine={false} tickLine={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey={barKey} name={barLabel} radius={[8, 8, 0, 0]}>
                {data.map((entry, i) => (
                  <Cell key={entry.name} fill={glassFill(i)} stroke={GLASS_STROKE} strokeWidth={1} />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey={lineKey}
                name={lineLabel}
                stroke="#0f172a"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#0f172a", strokeWidth: 2, stroke: "#fff" }}
                activeDot={{ r: 6 }}
                isAnimationActive
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </Panel>
  );
}
